import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Users, BookOpen, Heart, Shield, Brain } from "lucide-react";

const WEEKS = [
  { num: 1, title: "Nervous System 101", icon: Brain },
  { num: 2, title: "Substances: Functional to Dysfunctional", icon: Leaf },
  { num: 3, title: "Triggers: Top Down and Bottom Up", icon: Shield },
  { num: 4, title: "Progress Not Perfection", icon: BookOpen },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen" style={{ background: '#F7F5F0' }}>
      {/* Nav */}
      <nav className="glass-nav" data-testid="landing-nav">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 sm:px-12 py-5">
          <span className="text-xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>sober-curious.com</span>
          <div className="flex gap-3">
            <Link to="/login" className="earthy-btn earthy-btn-secondary text-sm" data-testid="nav-login-btn">Log In</Link>
            <Link to="/register" className="earthy-btn earthy-btn-primary text-sm" data-testid="nav-register-btn">Enroll Now</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 sm:px-12 lg:px-24 pt-20 pb-24" data-testid="hero-section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-in">
            <p className="text-sm font-medium uppercase tracking-[0.25em] mb-6 px-4 py-2 inline-block rounded-md" style={{ background: '#4A5D4E', color: '#F7F5F0', letterSpacing: '0.25em' }}>Online Programs for the Sober &amp; Seeking</p>
            <h1 className="text-5xl sm:text-6xl mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              sober-curious.com
            </h1>
            <p className="text-base sm:text-lg leading-relaxed mb-10" style={{ color: '#5B665E', maxWidth: 540 }}>
              Education-based group programs for people who recognize that using substances to manage life is no longer a viable or healthy option. No labels required. No need to identify as "alcoholic" or any other qualifier. Just honest, practical tools to understand what's happening in your body and what to do about it.
            </p>

            {/* Two Program Cards */}
            <p className="text-xs uppercase tracking-[0.2em] font-medium mb-4" style={{ color: '#A67C52' }}>Two Programs — Choose Your Path</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#causes" className="flex-1 rounded-lg p-5 transition-all duration-300 hover:-translate-y-1" style={{ background: '#4A5D4E', boxShadow: '0 8px 24px rgba(45,58,49,0.2)' }} data-testid="hero-causes-btn">
                <p className="text-xs uppercase tracking-[0.2em] font-medium mb-1" style={{ color: '#A67C52' }}>Enrolling Now</p>
                <p className="text-xl font-semibold mb-1" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#F7F5F0' }}>Causes &amp; Conditions</p>
                <p className="text-sm mb-3" style={{ color: 'rgba(247,245,240,0.7)' }}>Nervous system education for sobriety</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: '#F7F5F0' }}>Learn more <ArrowRight size={14} strokeWidth={1.5} /></span>
              </a>
              <a href="#sober-curious" className="flex-1 rounded-lg p-5 transition-all duration-300 hover:-translate-y-1" style={{ background: '#FFFFFF', border: '2px solid #D1CCC0', boxShadow: '0 8px 24px rgba(45,58,49,0.06)' }} data-testid="hero-sc-btn">
                <p className="text-xs uppercase tracking-[0.2em] font-medium mb-1" style={{ color: '#A67C52' }}>Coming November</p>
                <p className="text-xl font-semibold mb-1" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Sober Curious</p>
                <p className="text-sm mb-3" style={{ color: '#5B665E' }}>Exploring your relationship with substances</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: '#4A5D4E' }}>Learn more <ArrowRight size={14} strokeWidth={1.5} /></span>
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 fade-in stagger-2">
            <div className="rounded-lg overflow-hidden" style={{ boxShadow: '0 24px 64px rgba(45,58,49,0.12)' }}>
              <img 
                src="https://images.unsplash.com/photo-1759596436849-33b6e33e8ea4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxjYWxtJTIwcGVhY2VmdWwlMjBtb3JuaW5nJTIwbGlnaHQlMjBuYXR1cmV8ZW58MHx8fHwxNzgwOTQ2NjAzfDA&ixlib=rb-4.1.0&q=85"
                alt="Golden sunlight through tall grass" 
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* CAUSES & CONDITIONS — Active Program */}
      {/* ═══════════════════════════════════════════════ */}

      <section id="causes" className="px-6 sm:px-12 lg:px-24 py-20" style={{ background: '#FFFFFF' }}>
        <div className="max-w-4xl mx-auto">
          <p className="overline mb-4">Now Enrolling</p>
          <h2 className="text-4xl sm:text-5xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31', lineHeight: 1.1 }}>Causes &amp; Conditions</h2>
          <p className="text-2xl sm:text-3xl font-semibold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#4A5D4E' }}>Starts October 1, 2026</p>
          <p className="text-xl italic mb-8" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#4A5D4E' }}>
            Understand your nervous system: support your sober life.
          </p>
          <p className="text-base leading-relaxed mb-4" style={{ color: '#5B665E' }}>
            In mindfulness and psychology, "causes and conditions" is the practice of suspending judgment and looking objectively at the complex network of factors that led to where you are — then focusing only on what is within your control. That's exactly what this program does.
          </p>
          <p className="text-base leading-relaxed mb-4" style={{ color: '#5B665E' }}>
            If "taking the edge off" with a substance is no longer an option, this program offers a healthier alternative. You'll learn what's happening in your nervous system, why it got wired this way, and how to regulate it without the shortcuts.
          </p>
          <p className="text-base leading-relaxed mb-8" style={{ color: '#5B665E' }}>
            This is for anyone who recognizes that substances were doing a job — and now it's time to learn healthier ways to do that job yourself.
          </p>
        </div>
      </section>

      {/* This Is For You */}
      <section className="px-6 sm:px-12 lg:px-24 py-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl mb-12 text-center" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>This is for you if...</h2>
          <div className="grid sm:grid-cols-2 gap-x-12 gap-y-6">
            {[
              "You recognize that substances are no longer working for you and want healthier options",
              "You're hypervigilant and reactive and don't want to be",
              "You've traded one quick fix for another — food, scrolling, shopping",
              "You want to feel comfortable in your skin without reaching for a substance",
              "You want to relax but feel like your body won't cooperate, or you feel shut down and numb",
              "You want practical tools and evidence-based skills you can begin using immediately"
            ].map((text, i) => (
              <div key={i} className="flex gap-4 items-start fade-in" style={{ animationDelay: `${i * 0.05}s`, opacity: 0 }}>
                <Leaf size={18} strokeWidth={1.5} className="mt-1 flex-shrink-0" style={{ color: '#4A5D4E' }} />
                <p className="text-base" style={{ color: '#5B665E' }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Missing Piece */}
      <section className="px-6 sm:px-12 lg:px-24 py-20" style={{ background: '#FFFFFF' }}>
        <div className="max-w-3xl mx-auto text-center">
          <p className="overline mb-4">The Missing Piece</p>
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Choosing a sober lifestyle is the first step. Understanding your nervous system is what can help support it.</h2>
          <p className="text-base leading-relaxed" style={{ color: '#5B665E' }}>
            <strong style={{ color: '#2D3A31' }}>Causes &amp; Conditions</strong> teaches you what's happening in your body, why it's happening, and — most importantly — what to do about it. No clinical jargon. No judgment. Just real tools for real life.
          </p>
        </div>
      </section>

      {/* 6-Week Curriculum */}
      <section id="program" className="px-6 sm:px-12 lg:px-24 py-20" data-testid="program-overview">
        <div className="max-w-5xl mx-auto">
          <p className="overline text-center mb-4">The Curriculum</p>
          <h2 className="text-3xl sm:text-4xl mb-4 text-center" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>4 Weeks to a Settled Nervous System</h2>
          <p className="text-center mb-14 text-base" style={{ color: '#5B665E', maxWidth: 480, margin: '0 auto 3.5rem' }}>Each week builds on the last. By Week 4, you'll have a personal regulation toolkit that works.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WEEKS.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.num} className="earthy-card fade-in" style={{ animationDelay: `${w.num * 0.05}s`, opacity: 0 }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: 'rgba(74,93,78,0.1)' }}>
                      <Icon size={20} strokeWidth={1.5} style={{ color: '#4A5D4E' }} />
                    </div>
                    <span className="overline">Week {w.num}</span>
                  </div>
                  <h3 className="text-xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>{w.title}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="px-6 sm:px-12 lg:px-24 py-20" style={{ background: '#2D3A31' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl mb-12" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#F7F5F0' }}>What You Get</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: BookOpen, title: "Weekly Lessons", desc: "Bite-sized education about your nervous system that makes sense" },
              { icon: Shield, title: "Practical Tools", desc: "Exercises, worksheets, and techniques you'll use right away" },
              { icon: Users, title: "Group Support", desc: "A small cohort of people on the same path" },
              { icon: Heart, title: "Progress Tracking", desc: "Watch yourself grow and build skills week by week" }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <item.icon size={28} strokeWidth={1.5} className="mx-auto mb-4" style={{ color: '#A67C52' }} />
                <h4 className="font-medium mb-2 text-base" style={{ color: '#F7F5F0' }}>{item.title}</h4>
                <p className="text-sm" style={{ color: 'rgba(247,245,240,0.7)' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Aimee */}
      <section className="px-6 sm:px-12 lg:px-24 py-20" data-testid="about-section">
        <div className="max-w-4xl mx-auto grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-4 flex justify-center">
            <div className="w-56 h-56 rounded-lg overflow-hidden" style={{ background: '#EAE6DF', boxShadow: '0 16px 48px rgba(45,58,49,0.1)' }}>
              <img
                src="https://customer-assets.emergentagent.com/job_therapy-free-flow/artifacts/lv3r8jds_IMG_6270.jpeg"
                alt="Aimee Page, LPC"
                className="w-full h-full object-cover"
                data-testid="about-headshot"
              />
            </div>
          </div>
          <div className="md:col-span-8">
            <p className="overline mb-4">Meet Your Guide</p>
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Aimee Page, LPC, MS, NCC</h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#5B665E' }}>
              I'm a Board Certified, Licensed Professional Counselor with a Master's in Trauma Counseling from Thomas Jefferson University and a certification in Somatic Addiction Treatment.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#5B665E' }}>
              I've spent years working in substance abuse treatment and running my private psychotherapy practice. Along the way, I've facilitated hundreds of groups — and the most impactful were about the nervous system. They helped people connect the dots of why we use substances. The irritability. The restlessness. The shutdown. The being "triggered" and not knowing why. That's what this program is about.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: '#5B665E' }}>
              I believe that <strong style={{ color: '#2D3A31' }}>understanding your nervous system can help explain why we reach for things that numb, distract, or take the edge off</strong>. When the "why" starts to make sense, real change becomes possible.
            </p>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#5B665E' }}>
              I created these programs to make this education accessible outside of a therapy office — practical, grounded, and designed for people who are ready to feel at home in their own body.
            </p>
            <p className="text-sm font-medium" style={{ color: '#4A5D4E' }}>
              Questions? <a href="mailto:contact@sober-curious.com" className="underline hover:opacity-70 transition-opacity" data-testid="about-email-link">contact@sober-curious.com</a>
            </p>
          </div>
        </div>
      </section>

      {/* Causes & Conditions CTA */}
      <section className="px-6 sm:px-12 lg:px-24 py-24 text-center" style={{ background: '#EAE6DF' }}>
        <div className="max-w-2xl mx-auto">
          <p className="overline mb-4">Now Enrolling</p>
          <h2 className="text-3xl sm:text-4xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Causes &amp; Conditions</h2>
          <p className="text-3xl sm:text-4xl font-semibold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#4A5D4E' }}>Starts October 1, 2026</p>
          <p className="text-base mb-1" style={{ color: '#5B665E' }}>4 Weeks | $149</p>
          <p className="text-base mb-4" style={{ color: '#5B665E' }}>
            Understand your nervous system: support your sober life.
          </p>
          <Link to="/register" className="earthy-btn earthy-btn-primary text-lg inline-flex items-center gap-2" data-testid="cta-join-btn">
            Enroll for $149 <ArrowRight size={20} strokeWidth={1.5} />
          </Link>
          <p className="mt-4 text-sm" style={{ color: '#5B665E' }}>Secure checkout powered by Stripe</p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* SOBER CURIOUS — Coming Soon */}
      {/* ═══════════════════════════════════════════════ */}

      <section id="sober-curious" className="px-6 sm:px-12 lg:px-24 py-24" data-testid="sober-curious-section">
        <div className="max-w-3xl mx-auto text-center">
          <p className="overline mb-4">Coming November 2026</p>
          <h2 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31', lineHeight: 1.1 }}>Sober Curious</h2>
          <p className="text-xl italic mb-8" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#4A5D4E' }}>
            Exploring your relationship with substances — before it becomes a crisis.
          </p>
          <p className="text-base leading-relaxed mb-4" style={{ color: '#5B665E' }}>
            Not everyone who questions their drinking or substance use is in crisis. Some people are simply noticing that the way they use substances doesn't feel right anymore — the glass of wine that turned into a bottle, the edible that became a nightly ritual, the nagging feeling that you're using something to avoid something else.
          </p>
          <p className="text-base leading-relaxed mb-4" style={{ color: '#5B665E' }}>
            <strong style={{ color: '#2D3A31' }}>Sober Curious</strong> is for people who are ready to get honest about their relationship with substances — and curious about what life looks like without them. This program meets you where you are, without pressure and without labels.
          </p>
          <p className="text-base leading-relaxed mb-8" style={{ color: '#5B665E' }}>
            Details coming soon. Sign up to be the first to know.
          </p>
          <a href="mailto:contact@sober-curious.com?subject=Interested%20in%20Sober%20Curious%20program" className="earthy-btn earthy-btn-secondary text-base inline-flex items-center gap-2" data-testid="sober-curious-interest-btn">
            Get Notified When Enrollment Opens <ArrowRight size={18} strokeWidth={1.5} />
          </a>
        </div>
      </section>

      {/* Disclaimer + Footer */}
      <footer className="px-6 sm:px-12 py-12" style={{ background: '#2D3A31', color: 'rgba(247,245,240,0.6)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-8">
            <div>
              <p className="text-xl mb-1" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#F7F5F0' }}>sober-curious.com</p>
              <p className="text-sm">Education-based programs for the sober &amp; seeking</p>
            </div>
            <a href="mailto:contact@sober-curious.com" className="text-sm hover:text-white transition-colors" data-testid="footer-email">contact@sober-curious.com</a>
          </div>
          <div className="pt-6" style={{ borderTop: '1px solid rgba(247,245,240,0.15)' }}>
            <p className="text-xs leading-relaxed" style={{ color: 'rgba(247,245,240,0.5)' }}>
              <strong style={{ color: 'rgba(247,245,240,0.7)' }}>Important Disclaimer:</strong> Programs offered through sober-curious.com are psychoeducational and designed to provide information and practical tools for nervous system regulation. <strong style={{ color: 'rgba(247,245,240,0.7)' }}>These programs are not therapy.</strong> They are not a substitute for licensed mental health treatment, medical advice, or clinical intervention. Aimee Page, LPC, MS, NCC is a licensed clinician but is not acting in a therapeutic capacity within these programs. No therapist-client relationship is established through enrollment or participation. Participation does not require identifying as "alcoholic" or any other label. If you are in crisis or need immediate support, please contact SAMHSA's National Helpline at 1-800-662-4357 or dial 988 for the Suicide &amp; Crisis Lifeline.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
